import React, { useState } from 'react';
import { Button } from '@habitech/call-to-action';
import { TextField } from '@habitech/keychain-atoms-inputs';
import { useAppSelector } from 'src/hooks/redux';
import { selectUser } from 'src/redux/user/userSlice';
import { selectDataSuggested } from 'src/redux/shopping_cart/shopping_cart.selectors';
import { ProductByCategory, CommercialCartSelection } from 'src/redux/shopping_cart/shopping_cart.initial';
import { buildMercadoPagoPayload } from './mercadoPagoMapper';
import { createMercadoPagoPaymentLink, MercadoPagoSuccessResponse, parseMercadoPagoError } from 'src/apis/mercadoPago';
import { sendMail } from 'src/apis/sendMailApi';
import { SendMailRequest } from 'src/models/SendMail';
import Modal from 'src/components/common/modal/Modal';
import {
    PaymentCtaFooterNotice,
    WrapperError,
    WrapperModaPaymentOneClick,
} from 'src/components/ButtonPaymentOneClick/ButtonPaymentOneClick.styles';

export interface ButtonMercadoPagoProps {
    selectedProducts: ProductByCategory[];
    selection: Pick<CommercialCartSelection,
        'quantityByProductId' | 'priceByProductId' | 'msiEnabled' | 'msiMonths'
    >;
    selectedMonths: number | null;
    userId: number;
    payerEmail: string;
    userProfile: { name: string; lastname: string };
    buttonDisabled: boolean;
    ctaFooterNotice?: string;
}

const ButtonMercadoPago = ({
    selectedProducts,
    selection,
    selectedMonths,
    userId,
    payerEmail,
    userProfile,
    buttonDisabled,
    ctaFooterNotice,
}: ButtonMercadoPagoProps) => {
    const { token, email: loggedEmail, id: loggedId } = useAppSelector(selectUser);
    const { suggested } = useAppSelector(selectDataSuggested);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [modalData, setModalData] = useState<MercadoPagoSuccessResponse | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [isSending, setIsSending] = useState(false);

    const handleSubmit = async () => {
        if (loading) return; // double-submit guard
        if (selectedMonths === null) return; // guarded by canSubmit; defensive narrowing

        setLoading(true);
        setError('');

        try {
            const pipedriveSeller = suggested.pipedrive_seller ?? null;
            const loggedUser = { id: loggedId, email: loggedEmail };

            const payload = buildMercadoPagoPayload({
                selectedProducts,
                selection: {
                    quantityByProductId: selection.quantityByProductId,
                    priceByProductId: selection.priceByProductId,
                    msiEnabled: selection.msiEnabled,
                    msiMonths: selection.msiMonths,
                },
                selectedMonths,
                userId,
                userProfile,
                payerEmail,
                pipedriveSeller,
                loggedUser,
            });

            const { status, response } = await createMercadoPagoPaymentLink(payload, token);

            if (status === 200 && (response as MercadoPagoSuccessResponse).success === true) {
                const successResp = response as MercadoPagoSuccessResponse;
                setModalData(successResp);
                setShowModal(true);
            } else {
                setError(parseMercadoPagoError(status, response));
            }
        } catch {
            setError('Error inesperado. Intenta nuevamente.');
        } finally {
            setLoading(false);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
    };

    const handleCopy = () => {
        if (modalData?.init_point) {
            navigator.clipboard.writeText(modalData.init_point);
        }
    };

    const handleSendMail = async () => {
        if (isSending || !modalData) return;
        setIsSending(true);

        try {
            const mailPayload: SendMailRequest = {
                country: 'MX',
                name: 'orden_pago',
                data: {
                    name: userProfile.name,
                    order_id: modalData.external_reference,
                    payment_id: modalData.init_point,
                },
                destinataries: [payerEmail],
                subject: 'Link orden de pago',
            };
            await sendMail(mailPayload, token);
        } catch {
            // Mail failure does not block the flow
        } finally {
            setIsSending(false);
        }
    };

    return (
        <>
            <Button
                dataId="btn_mp_payment_link"
                onClick={handleSubmit}
                originType="pcom"
                size="large"
                disabled={buttonDisabled || loading}
                loading={loading}
            >
                Crear link de pago
            </Button>
            {ctaFooterNotice ? (
                <PaymentCtaFooterNotice role="status">{ctaFooterNotice}</PaymentCtaFooterNotice>
            ) : null}
            <WrapperError>
                {error ? (
                    <span role="alert">{error}</span>
                ) : null}
            </WrapperError>
            {showModal && modalData && (
                <Modal onClose={handleCloseModal}>
                    <WrapperModaPaymentOneClick>
                        <h1>Link de pago creado.</h1>
                        <p>
                            <b>Monto total:</b> ${modalData.amount_total} MXN
                        </p>
                        <p>
                            <b>Expira:</b> {modalData.expires_at}
                        </p>
                        <div className="WrapperUrl">
                            <TextField
                                label="Link de pago"
                                dataId="mp-init-point"
                                name="mp-init-point"
                                originType="pcom"
                                innerLabel
                                value={modalData.init_point}
                                disabled={true}
                            />
                            <button
                                className="btnCopy"
                                data-testid="btn-copy-mp-link"
                                onClick={handleCopy}
                            >
                                Copiar
                            </button>
                        </div>
                        <Button
                            dataId="btn-send-mail-mp"
                            onClick={handleSendMail}
                            originType="pcom"
                            size="extra-large"
                            fluid
                            disabled={isSending}
                            loading={isSending}
                        >
                            <span data-testid="btn-send-mail-mp">Enviar link al cliente</span>
                        </Button>
                    </WrapperModaPaymentOneClick>
                </Modal>
            )}
        </>
    );
};

export default ButtonMercadoPago;
